import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TestimonialDetailModal = ({ testimonial, onClose }) => {
  const hasCaseStudy = testimonial?.caseStudyContent && Object.keys(testimonial?.caseStudyContent)?.length > 0;
  const caseStudy = testimonial?.caseStudyContent;

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)]?.map((_, index) => (
          <Icon
            key={index}
            name="Star"
            size={16}
            className={index < rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300 dark:text-gray-600'}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 p-6 z-10">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{testimonial?.companyName}</h2>
                {testimonial?.isVerified && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-medium rounded">
                    <Icon name="CheckCircle" size={14} />
                    Verified Client
                  </span>
                )}
                {testimonial?.autoPublished && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium rounded">
                    <Icon name="Sparkles" size={14} />
                    AI-Generated Case Study
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                {renderStars(testimonial?.starRating)}
                <span className="text-sm text-gray-600 dark:text-gray-400">•</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">{testimonial?.industry}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Reviewer Info */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-semibold text-lg">
              {testimonial?.reviewerName?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">{testimonial?.reviewerName}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{testimonial?.reviewerPosition}</p>
            </div>
          </div>

          {/* Case Study Content */}
          {hasCaseStudy ? (
            <div className="space-y-6">
              {/* Executive Summary */}
              {caseStudy?.executive_summary && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Icon name="FileText" size={20} className="text-emerald-600" />
                    Executive Summary
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{caseStudy?.executive_summary}</p>
                </div>
              )}

              {/* Challenge */}
              {caseStudy?.challenge && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Icon name="AlertCircle" size={20} className="text-orange-600" />
                    The Challenge
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{caseStudy?.challenge}</p>
                </div>
              )}

              {/* Solution */}
              {caseStudy?.solution && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Icon name="Lightbulb" size={20} className="text-blue-600" />
                    The Solution
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{caseStudy?.solution}</p>
                </div>
              )}

              {/* Implementation Timeline */}
              {caseStudy?.implementation_timeline && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Icon name="Calendar" size={20} className="text-purple-600" />
                    Implementation Timeline
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{caseStudy?.implementation_timeline}</p>
                </div>
              )}

              {/* Quantifiable Results */}
              {caseStudy?.quantifiable_results && caseStudy?.quantifiable_results?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Icon name="TrendingUp" size={20} className="text-emerald-600" />
                    Quantifiable Results
                  </h3>
                  <div className="space-y-3">
                    {caseStudy?.quantifiable_results?.map((result, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                        <p className="font-medium text-gray-900 dark:text-white mb-2">{result?.metric}</p>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">Before</p>
                            <p className="font-semibold text-gray-900 dark:text-white">{result?.before}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">After</p>
                            <p className="font-semibold text-gray-900 dark:text-white">{result?.after}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">Improvement</p>
                            <p className="font-semibold text-emerald-600 dark:text-emerald-400">{result?.improvement}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ROI Calculation */}
              {caseStudy?.roi_calculation && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Icon name="DollarSign" size={20} className="text-green-600" />
                    ROI Analysis
                  </h3>
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg p-6">
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Savings</p>
                        <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                          K{(caseStudy?.roi_calculation?.total_savings_kina || 0)?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">ROI</p>
                        <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                          {caseStudy?.roi_calculation?.roi_percentage || 0}%
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Payback Period</p>
                        <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                          {caseStudy?.roi_calculation?.payback_period_months || 0} mo
                        </p>
                      </div>
                    </div>
                    {caseStudy?.roi_calculation?.calculation_details && (
                      <p className="text-sm text-gray-700 dark:text-gray-300">{caseStudy?.roi_calculation?.calculation_details}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Key Takeaways */}
              {caseStudy?.key_takeaways && caseStudy?.key_takeaways?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Icon name="CheckCircle" size={20} className="text-blue-600" />
                    Key Takeaways
                  </h3>
                  <ul className="space-y-2">
                    {caseStudy?.key_takeaways?.map((takeaway, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                        <Icon name="Check" size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{takeaway}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Testimonial Quote */}
              {caseStudy?.testimonial_quote && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600 p-6 rounded-r-lg">
                  <Icon name="Quote" size={32} className="text-blue-600 mb-3" />
                  <p className="text-gray-900 dark:text-white italic text-lg mb-4">"{caseStudy?.testimonial_quote}"</p>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{caseStudy?.testimonial_author || testimonial?.reviewerName}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{caseStudy?.testimonial_position || testimonial?.reviewerPosition}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Original Review Display */
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Client Review</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">{testimonial?.reviewText}</p>
            </div>
          )}

          {/* Compliance Metrics */}
          <div className="flex items-center gap-4 mb-4 text-xs text-gray-500 dark:text-gray-500">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="DollarSign" size={28} className="text-green-600 dark:text-green-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">K{(testimonial?.costSavingsKina || 0)?.toLocaleString()}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Cost Savings</p>
          </div>

          <div className="flex items-center gap-4 mb-4 text-xs text-gray-500 dark:text-gray-500">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="TrendingUp" size={28} className="text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{testimonial?.timeEfficiencyGainPercentage || 0}%</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Efficiency Gain</p>
          </div>

          <div className="flex items-center gap-4 mb-4 text-xs text-gray-500 dark:text-gray-500">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="Shield" size={28} className="text-purple-600 dark:text-purple-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{testimonial?.regulatoryAdherenceScore || 0}%</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Compliance Score</p>
          </div>

          {/* Organization Details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600 dark:text-gray-400 mb-1">Department Type</p>
              <p className="font-medium text-gray-900 dark:text-white capitalize">{testimonial?.departmentType || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 dark:text-gray-400 mb-1">Organization Size</p>
              <p className="font-medium text-gray-900 dark:text-white capitalize">{testimonial?.organizationSize || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 dark:text-gray-400 mb-1">Primary Use Case</p>
              <p className="font-medium text-gray-900 dark:text-white capitalize">{testimonial?.useCase || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600 dark:text-gray-400 mb-1">Published Date</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {testimonial?.publishedAt ? new Date(testimonial?.publishedAt)?.toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-slate-700 flex items-center justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default TestimonialDetailModal;